import type { CardId, GameEvent, PublicMatchView, RulesConfig, Seat } from '@mendikot/engine';

export type SeatKey = `s${number}`;
export const seatKey = (seat: Seat): SeatKey => `s${seat}`;
export function parseSeatKey(key: string): Seat | null {
  const match = /^s(\d)$/.exec(key);
  return match ? Number(match[1]) : null;
}

export type RoomStatus = 'LOBBY' | 'PLAYING' | 'MATCH_OVER' | 'ABANDONED';

export interface SeatInfo {
  seat: Seat;
  /** Firebase uid of a human seat; null for permanent bots. */
  uid: string | null;
  name: string;
  avatarId: string;
  kind: 'human' | 'bot';
  /** A human seat is bot-controlled while away; never silently forfeited. */
  controller: 'human' | 'bot';
  ready: boolean;
  timeouts: number;
}

export type BotTakeoverReason = 'TIMEOUTS' | 'DISCONNECTED' | 'LEFT';

/** Engine events plus room-level events, all ordered within one commit. */
export type HostEvent =
  | GameEvent
  | { t: 'MATCH_STARTED'; matchId: string; rules: RulesConfig }
  | { t: 'AUTO_PLAYED'; seat: Seat; reason: 'TIMEOUT' }
  | { t: 'BOT_TAKEOVER'; seat: Seat; reason: BotTakeoverReason }
  | { t: 'PLAYER_RETURNED'; seat: Seat }
  | { t: 'ROOM_ABANDONED'; reason: string };

export interface EventEnvelope {
  /** Position within the commit; public and private events interleave by `n`. */
  n: number;
  e: HostEvent;
}

/** Events produced by the commit that moved the room from `fromV` to `toV`. */
export interface Batch {
  fromV: number;
  toV: number;
  events: EventEnvelope[];
}

export interface RoomPublicView {
  version: number;
  roomId: string;
  code: string;
  hostUid: string;
  status: RoomStatus;
  rules: RulesConfig;
  seats: (SeatInfo | null)[];
  game: PublicMatchView | null;
  /** Server-clock deadline for the active human seat; null when untimed. */
  turnDeadline: number | null;
  /** Earliest server time the next hand may be dealt without every human agreeing. */
  nextHandAt: number | null;
  nextVotes: boolean[];
  rematchVotes: (boolean | null)[];
  /** Turn already nudged (`handId:trick:order`) — max one push per turn. */
  nudgeKey: string | null;
  abandonReason: string | null;
  updatedAt: number;
  batch: Batch;
}

export interface RoomPrivateView {
  version: number;
  seat: Seat;
  hand: CardId[];
  reserved: CardId | null;
  batch: Batch;
}

/** RTDB storage shape: JSON text avoids RTDB's array/undefined/empty-node quirks entirely. */
export interface StoredView {
  v: number;
  data: string;
}
